'use client';
import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Home, ArrowLeft, Search, Trophy } from 'lucide-react';

const QUICK_LINKS = [
  { label: 'Browse Olympiads', href: '/all-olympiads' },
  { label: 'Student Dashboard', href: '/student-dashboard' },
  { label: 'Leaderboard', href: '/leaderboard' },
  { label: 'Blogs & Resources', href: '/blogs' },
];

export default function NotFound() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Minimal header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Trophy size={16} className="text-white" />
            </div>
            <span className="font-extrabold text-lg text-primary tracking-tight">
              FutureMinds<span className="text-secondary">.ai</span>
            </span>
          </Link>
          <Link href="/" className="text-sm text-muted-foreground hover:text-primary transition-colors font-medium">
            Back to Home
          </Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 pt-16">
        <div className="max-w-lg w-full text-center">
          {/* 404 visual */}
          <div className="relative mb-8">
            <div className="text-[120px] md:text-[160px] font-extrabold leading-none select-none" style={{ color: 'var(--primary)', opacity: 0.08 }}>
              404
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Search size={36} className="text-primary" />
              </div>
            </div>
          </div>

          <h1 className="text-2xl md:text-3xl font-extrabold text-foreground mb-3">
            Page Not Found
          </h1>
          <p className="text-muted-foreground text-sm leading-relaxed mb-8 max-w-sm mx-auto">
            The page you&apos;re looking for doesn&apos;t exist or may have been moved. Let&apos;s get you back on track.
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
            <button
              onClick={() => router?.back()}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border bg-card text-foreground rounded-xl font-semibold text-sm hover:bg-muted transition-colors"
            >
              <ArrowLeft size={16} />
              Go Back
            </button>
            <Link
              href="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold text-sm hover:bg-primary/90 transition-colors"
            >
              <Home size={16} />
              Back to Home
            </Link>
          </div>

          {/* Quick links */}
          <div className="border-t border-border pt-8">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">Quick Links</p>
            <div className="flex flex-wrap justify-center gap-2">
              {QUICK_LINKS?.map((link) => (
                <Link
                  key={link?.label}
                  href={link?.href}
                  className="px-4 py-2 bg-card border border-border rounded-xl text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/30 transition-colors"
                >
                  {link?.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </main>
      <footer className="py-6 text-center border-t border-border">
        <p className="text-xs text-muted-foreground">
          © 2026 FutureMinds.ai · <Link href="/contact" className="hover:text-primary transition-colors">Contact Support</Link>
        </p>
      </footer>
    </div>
  );
}